import * as React from 'react'
import PropTypes from 'prop-types'
import Script from 'next/script'
import clsx from 'clsx'
import Media from '@oakwood/oui/Media'
import MediaReveal from '@oakwood/oui/MediaReveal'
import getConfig from 'next/config'
import makeStyles from '@material-ui/core/styles/makeStyles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useRouter } from 'next/router'
import Button from 'components/Button'
import Container from 'components/Container'
import Html from 'components/Html'
import Section from 'components/Section'
import { useGlobal } from 'api'
import Typography from 'components/Typography'
import { mediaType } from 'utils'

const BREAKPOINT_KEY_UP = 'sm'

export const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.common.black,
  },
  firstStep: {
    minHeight: 'calc(100vh - 50px)',
  },
  textColorInverted: {
    color: theme.palette.common.white,
  },
  backgroundMediaContainer: {
    ...theme.mixins.absolute(0),
    zIndex: -150,
  },
  backgroundMedia: {
    height: '100%',
  },
  main: {
    ...theme.mixins.verticalRhythm(3),
    maxWidth: '640px',
    display: 'flex',
    justifyContent: 'center',
    '-webkit-box-pack': 'center',
    flexDirection: 'column',
  },
  cta: {
    maxWidth: 390,
  },
  ctaWrap: {
    display: 'flex',
    width: '100%',
  },
  defaultBackgroundColor: {
    '& > #dt-pg-root': {
      '--background-color': theme.palette.background.default,
    },
  },
  contentTitle: {
    textAlign: 'center',
    fontSize: 24,
  },
  dtGuide: {
    display: 'none',
    width: '100%',
    height: '100%',
  },
  dtGuideShow: {
    display: 'block',
  },
}))

function DialogTrail(props) {
  const {
    fullScreen,
    backgroundMediaProps,
    backgroundMediaMobile,
    title,
    titleSize = 'medium',
    description,
    descriptionSize = 'medium',
    ctaType = 'contained',
    ctaLabel,
    ctaAlign = 'center',
    ctaButtonSize = 'medium',
  } = props
  const { publicRuntimeConfig } = getConfig()

  const classes = useStyles(props)

  const { market, language } = useGlobal()
  const [step, setStep] = React.useState(0)
  const stepRef = React.useRef(0)
  const dtCurrentStepRef = React.useRef(null)
  const dtPrevStepRef = React.useRef(null)
  const sectionRef = React.useRef(null)

  const router = useRouter()

  const scrollToDialogTrail = () => {
    // This fixes the 'jumpy' behavior throughout the quiz
    const section = sectionRef?.current
    const scrollOffset = section?.offsetTop - 46

    if (section?.offsetTop) {
      window.scrollTo({
        top: scrollOffset,
        behavior: 'smooth',
      })
    }
  }

  const dtSubscribe = () => {
    window.DT?.subscribe('product_guide', (e) => {
      if (stepRef.current !== 0) scrollToDialogTrail()

      dtCurrentStepRef.current = e.step?.variant

      if (dtCurrentStepRef.current !== 'result') dtPrevStepRef.current = 'prev'

      if (dtPrevStepRef.current === 'prev' && dtCurrentStepRef.current === 'result') {
        dtPrevStepRef.current = 'result'
        const codes = e.products?.productItems
          ?.map((product) => product?.id?.substring(0, 3))
          .join(',')

        const name = e.status?.steps?.[0]?.userInputs?.[0]?.input
        const goals = e.status?.steps?.[3]?.options?.join(', ')

        if (codes !== '') {
          router.push({
            pathname: '/all-products',
            query: {
              codes,
              name,
              goals,
            },
          })
        }
      }
    })
  }

  React.useEffect(() => {
    if (step !== 0) stepRef.current = 1
  }, [step])

  const isBreakpointUp = useMediaQuery((theme) => theme.breakpoints.up(BREAKPOINT_KEY_UP))

  let mobileMedia

  if (backgroundMediaMobile?.breakpoints) {
    mobileMedia = true
  } else if (backgroundMediaMobile?.src) {
    mobileMedia = true
  } else {
    mobileMedia = false
  }

  React.useEffect(() => {
    if (document && document.body.classList.contains('dt-overflow-hidden')) {
      document.body.classList.remove('dt-overflow-hidden')
    }
  }, [])

  return (
    <Section
      className={clsx(classes.root, {
        [classes.firstStep]: step === 0 && fullScreen,
      })}
      ref={sectionRef}
    >
      <Script
        src={`https://headless.dialogtrail.com/static/sdk.js?id=${
          publicRuntimeConfig.DIALOG_TRAIL_ID
        }&lang=${language.langCode}&locale=${market.code === 'eu' ? 'us' : market.code}`}
        strategy="afterInteractive"
        id="dt-script"
        onLoad={() => {
          dtSubscribe()
        }}
      />
      {step === 0 && (
        <>
          <MediaReveal className={classes.backgroundMediaContainer}>
            {(isBreakpointUp && backgroundMediaProps) || !mobileMedia ? (
              <Media className={classes.backgroundMedia} {...backgroundMediaProps} />
            ) : (
              <Media className={classes.backgroundMedia} {...backgroundMediaMobile} />
            )}
          </MediaReveal>
          <Container className={clsx(classes.main)} maxWidth="md">
            {title && (
              <Typography
                variant="h1"
                className={classes.contentTitle}
                style={{ fontSize: titleSize }}
              >
                {title}
              </Typography>
            )}
            {description && (
              <Html
                style={{ textAlign: 'center', fontSize: descriptionSize }}
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}

            {ctaLabel && (
              <div className={classes.ctaWrap} style={{ justifyContent: ctaAlign }}>
                <Button
                  className={classes.cta}
                  variant={ctaType}
                  id="kalla-dt-event"
                  onClick={() => {
                    setStep(1)
                    scrollToDialogTrail()
                  }}
                  size={ctaButtonSize}
                >
                  {ctaLabel}
                </Button>
              </div>
            )}
          </Container>
        </>
      )}

      <div
        id="dt-product-guide"
        className={clsx(classes.dtGuide, {
          [classes.dtGuideShow]: step === 1,
        })}
      />
    </Section>
  )
}

DialogTrail.propTypes = {
  fullScreen: PropTypes.bool,
  backgroundMediaProps: mediaType,
  backgroundMediaMobile: mediaType,
  title: PropTypes.string,
  titleSize: PropTypes.string,
  description: PropTypes.string,
  descriptionSize: PropTypes.string,
  ctaType: PropTypes.string,
  ctaLabel: PropTypes.string,
  ctaAlign: PropTypes.string,
  ctaButtonSize: PropTypes.string,
}

export default DialogTrail
